import EntityModel from '@/common/models/entity-model';
import RuleActionModel from '@/common/models/rules/actions/rule-action-model';

export class EmailAttachmentModel extends EntityModel {
    hydrate({
        resourceType = null,
        resourceId = null,
    } = {}) {
        this.resourceType = resourceType;
        this.resourceId = resourceId;
    }

    getPayload() {
        return {
            resourceType: this.resourceType,
            resourceId: this.resourceId,
        };
    }
}

export class EmailModel extends EntityModel {
    hydrate({
        id = null,
        credentialHash = null,
        credentials = null,
        version = null,
        from = null,
        to = null,
        cc = [],
        bcc = [],
        subject = null,
        text = '',
        html = '',
        attachments = [],
        weight = 100,
    } = {}) {
        this.id = id;
        this.credentialHash = credentialHash;
        this.version = version;
        this.from = from;
        this.to = to;
        this.cc = cc;
        this.bcc = bcc;
        this.subject = subject;
        this.text = text;
        this.html = html;
        this.attachments = attachments.map(v => new EmailAttachmentModel(v));
        this.weight = Number(weight);
        // property used to store an instance of EmailCredentialModel in the UI
        this.$credentials = credentials;
    }

    cloneSelf(fields = {}, dirty = true) {
        // handle internal credential tracking
        // by always passing it if not in the field data
        if (fields.credentials === undefined) {
            fields.credentials = this.$credentials;
        }
        return super.cloneSelf(fields, dirty);
    }

    getPayload() {
        return {
            id: this.id,
            credentialHash: this.credentialHash,
            version: this.version,
            from: this.from,
            to: this.to,
            cc: this.cc,
            bcc: this.bcc,
            subject: this.subject,
            text: this.text || '',
            html: this.html || '',
            attachments: this.attachments.map(v => v.getPayload()),
            weight: Number(this.weight),
        };
    }

    get hasCc() {
        return this.cc.length > 0;
    }

    get hasBcc() {
        return this.bcc.length > 0;
    }

    get hasAttachedInvoice() {
        return this.attachments.length > 0;
    }

    get senders() {
        return this.from ? [this.from] : [];
    }

    get receivers() {
        return [...new Set([...(this.to || []), ...this.cc, ...this.bcc])];
    }
}

export default class SendEmailActionModel extends RuleActionModel {
    hydrate(fields = {}) {
        super.hydrate(fields);
        const {
            id = null,
            title = null,
            emails = [],
            splitTestStartTime = null,
        } = fields;

        // Include at least one empty email.
        if (!emails.length) {
            emails.push({});
        }

        this.id = id;
        this.title = title;
        this.emails = emails.map(v => new EmailModel(v));
        this.splitTestStartTime = splitTestStartTime;
    }

    recalculateWeights() {
        const emailsTotal = this.emails.length;
        this.emails.forEach((email) => {
            email.weight = Math.round((1 / emailsTotal) * 100);
        });
    }

    cloneSelf(fields = {}, dirty = true) {
        // make sure emails run cloneSelf instead of their payload logic
        // to keep their internal data
        fields.emails = this.emails.map((v) => {
            const copy = v.cloneSelf();
            if (v.$credentials !== undefined) {
                // store for hydration of email model
                copy.credentials = v.$credentials;
            }
            return copy;
        });
        return super.cloneSelf(fields, dirty);
    }

    getPayload() {
        return {
            ...super.getPayload(),
            id: this.id,
            title: this.title,
            emails: this.emails.map(v => v.getPayload()),
        };
    }
}
